// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressBar, StreakCounter, BadgeDisplay, PremiumBanner, LevelBadge, GlobalProgressBar } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { BookOpen, ChevronRight, Trophy, Target, Flame, CheckCircle, X, PieChart, Shield, TrendingUp, FileText, Map, Download, Calculator, Users, Copy, Gift } from 'lucide-react';
import { toast } from 'sonner';
import PercorsoPersonalizzato from '../components/PercorsoPersonalizzato';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ReferralPanel({ user }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetch(`${API_URL}/api/user/referral`, { credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setInfo(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const copyLink = () => {
    if (!info?.referral_link) return;
    navigator.clipboard.writeText(info.referral_link).then(() => {
      setCopied(true);
      toast.success('Link copiato!');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (loading) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }} className="sapiens-card p-6" data-testid="referral-panel">
      <h2 className="text-lg font-medium text-[#E8E8E4] mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5 text-[#3D7A52]" />Il mio programma referral
      </h2>
      {!info ? (
        <p className="text-sm text-[#888780]">Accedi per vedere il tuo codice referral.</p>
      ) : (
        <div className="space-y-4">
          {/* Referral days remaining */}
          {info.referral_premium_days > 0 && (
            <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: 'rgba(61,122,82,0.1)', border: '1px solid rgba(61,122,82,0.25)' }}>
              <CheckCircle className="w-4 h-4 text-[#3D7A52] shrink-0" />
              <p className="text-sm text-[#3D7A52]"><strong>{info.referral_premium_days} giorni Premium gratuiti</strong> ancora attivi sul tuo account!</p>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl text-center" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-2xl font-bold text-[#C9A84C]">{info.referred_count || 0}</p>
              <p className="text-xs text-[#888780] mt-0.5">Amici invitati</p>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-2xl font-bold text-[#3D7A52]">{info.total_days_earned || 0}</p>
              <p className="text-xs text-[#888780] mt-0.5">Giorni guadagnati</p>
            </div>
          </div>

          {/* Link copy */}
          <div>
            <p className="text-xs text-[#888780] mb-2">Il tuo link di invito:</p>
            <div className="flex gap-2">
              <div className="flex-1 px-3 py-2 rounded-lg text-xs font-mono text-[#888780] truncate" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
                {info.referral_link}
              </div>
              <button
                onClick={copyLink}
                data-testid="copy-referral-link"
                className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all"
                style={{ background: copied ? 'rgba(61,122,82,0.15)' : 'rgba(201,168,76,0.1)', border: `1px solid ${copied ? 'rgba(61,122,82,0.4)' : 'rgba(201,168,76,0.3)'}`, color: copied ? '#3D7A52' : '#C9A84C' }}
              >
                <Copy className="w-3 h-3" />{copied ? 'Copiato!' : 'Copia'}
              </button>
            </div>
          </div>

          <p className="text-xs text-[#888780]" style={{ lineHeight: 1.6 }}>
            <strong className="text-[#E8E8E4]">Come funziona:</strong> Condividi il tuo link. Ogni nuovo utente che si registra tramite il tuo link riceve 30 giorni Premium gratis — e anche tu ricevi 30 giorni Premium aggiuntivi!
          </p>
        </div>
      )}
    </motion.div>
  );
}

function MieiCertificati({ user }) {
  const [certs, setCerts] = useState([]);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/certificates`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCerts(d.certificates || []); })
      .catch(() => {});
  }, [user]);

  const downloadCert = async (level) => {
    setDownloading(level);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/user/certificate/${level}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `SAPIENS_Certificato_L${level}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Certificato non disponibile — completa prima tutte le lezioni del livello.'); }
    finally { setDownloading(null); }
  };

  if (!certs.length) return null;
  const levelNames = { 1: 'Base', 2: 'Intermedio', 3: 'Avanzato', 4: 'Esperto' };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="sapiens-card p-6" data-testid="miei-certificati">
      <h2 className="text-lg font-medium text-[#E8E8E4] mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#C9A84C]" />I miei certificati</h2>
      <div className="space-y-3">
        {certs.map(cert => (
          <div key={cert.level} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
            <div>
              <div className="text-sm font-medium text-[#E8E8E4]">Livello {cert.level} — {levelNames[cert.level]}</div>
              <div className="text-xs text-[#888780]">{new Date(cert.earned_at).toLocaleDateString('it-IT')}</div>
            </div>
            <button onClick={() => downloadCert(cert.level)} disabled={downloading === cert.level}
              className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:opacity-80 disabled:opacity-50"
              style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#C9A84C' }}
              data-testid={`download-cert-${cert.level}`}>
              <Download className="w-3 h-3" />{downloading === cert.level ? '...' : 'Scarica PDF'}
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function MieiCalcoli({ user, navigate }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (user?.tier !== 'premium') return;
    setLoading(true);
    fetch(`${API_URL}/api/user/calculator-history`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setHistory(d.calculations || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (user?.tier !== 'premium') return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="sapiens-card p-6" data-testid="miei-calcoli-locked">
      <h2 className="text-lg font-medium text-[#E8E8E4] mb-3 flex items-center gap-2"><Calculator className="w-5 h-5 text-[#888780]" />I miei calcoli</h2>
      <p className="text-sm text-[#888780] mb-3">Salva e ritrova tutti i tuoi calcoli. Disponibile con Premium.</p>
      <button onClick={() => navigate('/premium')} className="text-xs px-3 py-1.5 rounded-lg font-medium" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', color: '#C9A84C' }}>Scopri SAPIENS Premium →</button>
    </motion.div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="sapiens-card p-6" data-testid="miei-calcoli">
      <h2 className="text-lg font-medium text-[#E8E8E4] mb-4 flex items-center gap-2"><Calculator className="w-5 h-5 text-[#C9A84C]" />I miei calcoli</h2>
      {loading && <p className="text-sm text-[#888780]">Caricamento...</p>}
      {!loading && history.length === 0 && <p className="text-sm text-[#888780]">Nessun calcolo salvato ancora. Usa i calcolatori per iniziare.</p>}
      {!loading && history.length > 0 && (
        <div className="space-y-2">
          {history.slice(0, 5).map(c => (
            <div key={c.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`calc-history-${c.id}`}>
              <div>
                <div className="text-sm font-medium text-[#E8E8E4]">{c.calculator_name}</div>
                <div className="text-xs text-[#888780]">{c.key_result} · {new Date(c.date).toLocaleDateString('it-IT')}</div>
              </div>
              <button onClick={() => navigate(`/calcolatori/${c.calculator_slug}`)} className="text-xs px-2 py-1 rounded" style={{ color: '#C9A84C', border: '1px solid rgba(201,168,76,0.2)' }}>Rifai →</button>
            </div>
          ))}
          {history.length > 5 && <p className="text-xs text-[#888780] text-center mt-2">+{history.length - 5} altri calcoli</p>}
        </div>
      )}
    </motion.div>
  );
}

const AnimatedCounter = ({ value, duration = 1000, suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime; let animationFrame;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));
      if (progress < 1) animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  return <span className="tabular-nums">{displayValue}{suffix}</span>;
};

const ONBOARDING_STEPS = [
  {
    step: 1, icon: PieChart, iconBg: '#3D7A52',
    title: 'Calcola il tuo budget mensile',
    desc: 'Scopri quanto risparmi ogni mese',
    cta: 'Apri Budget mensile →',
    link: '/calcolatori?calc=budget',
    badge: 'Inizia qui',
    badgeStyle: { background: 'rgba(61,122,82,0.2)', color: '#9FE1CB', border: '1px solid #3D7A52' },
  },
  {
    step: 2, icon: Shield, iconBg: '#4A5568',
    title: 'Costruisci il fondo emergenza',
    desc: '3-6 mesi di spese al sicuro',
    cta: 'Calcola il tuo fondo →',
    link: '/calcolatori?calc=emergency',
  },
  {
    step: 3, icon: TrendingUp, iconBg: '#3D7A52',
    title: 'Investi il tuo risparmio mensile',
    desc: 'Fai lavorare i soldi risparmiati',
    cta: 'Simula la crescita →',
    link: '/calcolatori?calc=pac',
    note: 'Usa il risparmio del Step 1 come importo mensile',
    isPremiumStep: true,
  },
  {
    step: 4, icon: Flame, iconBg: '#C9A84C',
    title: 'Pianifica la tua libertà finanziaria',
    desc: 'Calcola quando puoi smettere di lavorare',
    cta: 'Scopri il tuo FIRE →',
    link: '/calcolatori?calc=fire',
  },
];

const OnboardingBanner = ({ user, overallProgress, onDismiss }) => {
  const navigate = useNavigate();
  const [localSteps, setLocalSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sap_onboarding_steps') || '[]'); } catch { return []; }
  });
  const completedSteps = user?.onboarding_steps || localSteps;

  // Re-sync localSteps on focus (after returning from calcolatori)
  useEffect(() => {
    const onFocus = () => {
      try { setLocalSteps(JSON.parse(localStorage.getItem('sap_onboarding_steps') || '[]')); } catch { /* */ }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (user?.onboarding_dismissed || overallProgress > 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      data-testid="onboarding-banner"
      style={{
        background: 'linear-gradient(135deg, #1A3A2A 0%, #252523 100%)',
        border: '1px solid rgba(201,168,76,0.3)',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24,
        position: 'relative',
      }}
    >
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        data-testid="onboarding-dismiss-btn"
        style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', cursor: 'pointer', color: '#888780', padding: 4 }}
        className="hover:text-[#E8E8E4] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <h3 style={{ color: '#C9A84C', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Da dove iniziare?</h3>
      <p style={{ color: '#888780', fontSize: 14, marginBottom: 20 }}>Ti guidiamo passo dopo passo verso la tua salute finanziaria</p>

      {/* Steps */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {ONBOARDING_STEPS.map((s) => {
          const Icon = s.icon;
          const done = completedSteps.includes(s.step);
          return (
            <div
              key={s.step}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: done ? '1px solid #3D7A52' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                padding: '14px 16px',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: done ? '#3D7A52' : s.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {done ? <CheckCircle className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
                </div>
                <span style={{ color: done ? '#888780' : '#E8E8E4', fontSize: 13, fontWeight: 600 }}>{s.title}</span>
              </div>
              <p style={{ color: '#888780', fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>{s.desc}</p>
              <button
                onClick={() => navigate(s.link)}
                style={{ color: '#C9A84C', fontSize: 12, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                className="hover:text-[#E8E8E4] transition-colors"
                data-testid={`onboarding-step-${s.step}-cta`}
              >
                {s.cta}
              </button>
              {s.note && <p style={{ color: '#888780', fontSize: 11, fontStyle: 'italic', marginTop: 4 }}>{s.note}</p>}
              {s.badge && !done && <span style={{ ...s.badgeStyle, fontSize: 10, padding: '2px 6px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>{s.badge}</span>}
              {s.isPremiumStep && user?.tier !== 'premium' && !done && (
                <span style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C', fontSize: 10, padding: '2px 6px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>Upgrade Premium</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-3 mt-4">
        <span style={{ color: '#888780', fontSize: 12 }}>{completedSteps.length} di 4 step completati</span>
        <div className="flex gap-1">
          {ONBOARDING_STEPS.map(s => (
            <div key={s.step} style={{ width: 20, height: 4, borderRadius: 2, background: completedSteps.includes(s.step) ? '#3D7A52' : 'rgba(255,255,255,0.1)' }} />
          ))}
        </div>
      </div>
    </motion.div>
  );
};
const DashboardPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPercorso, setShowPercorso] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    !!(typeof window !== 'undefined' && localStorage.getItem('sap_onboarding_dismissed'))
  );

  const handleDownloadPDF = async () => {
    if (user?.tier !== 'premium') { toast.error('Questa funzione è riservata ai membri Premium'); return; }
    setPdfLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user/report/pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error('Errore generazione PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SAPIENS_Report_${user?.name?.split(' ')[0] || 'Utente'}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report PDF scaricato!');
    } catch { toast.error('Errore nella generazione del report'); }
    finally { setPdfLoading(false); }
  };

  const handleDismissOnboarding = useCallback(async () => {
    setOnboardingDismissed(true);
    if (user) {
      try {
        await fetch(`${API_URL}/api/user/onboarding`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ dismissed: true }),
        });
      } catch (e) { /* ignore */ }
    } else {
      localStorage.setItem('sap_onboarding_dismissed', '1');
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressRes, lessonsRes] = await Promise.all([
          fetch(`${API_URL}/api/user/progress`, { credentials: 'include' }),
          fetch(`${API_URL}/api/lessons`, { credentials: 'include' })
        ]);
        if (progressRes.ok) setProgress(await progressRes.json());
        if (lessonsRes.ok) setLessons(await lessonsRes.json());
      } catch (error) { console.error('Error fetching dashboard data:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleUpgrade = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/upgrade`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Upgrade effettuato con successo!'); refreshUser(); }
    } catch (error) { toast.error('Errore durante l\'upgrade'); }
  };

  const allBadges = ['primo_passo', 'studente_base', 'investitore_junior', 'esperto_portafoglio', 'maestro_finanza', 'quiz_master', 'perfezionista', 'costante', 'maratoneta'];
  const realLessons = lessons.filter(l => !l.is_level_quiz);
  const uncompletedLessons = realLessons.filter(l => !l.completed).slice(0, 3);
  const levelGroups = [1, 2, 3, 4].map(level => ({ level, lessons: realLessons.filter(l => l.level === level), progress: progress?.by_level?.[`level_${level}`] || { percentage: 0, completed: 0, total: 0 } }));

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]"><div className="w-12 h-12 border-4 border-[#3D7A52] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-[#1C1C1A]" data-testid="dashboard-page">
      <GlobalProgressBar value={progress?.overall?.percentage || 0} />
      <div className="container-wide py-8 pt-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-medium text-[#E8E8E4] mb-2">Ciao, {user?.name?.split(' ')[0]}! 👋</h1>
            <p className="text-[#888780]">{user?.tier === 'premium' ? <span className="premium-badge">Premium</span> : 'Continua il tuo percorso di apprendimento'}</p>
          </div>
          {progress && <StreakCounter streak={progress.streak} />}
        </motion.div>

        {/* Onboarding Banner */}
        {!onboardingDismissed && (
          <OnboardingBanner
            user={user}
            overallProgress={progress?.overall?.percentage || 0}
            onDismiss={handleDismissOnboarding}
          />
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-3 gap-4 mb-8">
          <div className="sapiens-card p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#2A4A35] flex items-center justify-center mx-auto mb-4"><Target className="w-6 h-6 text-[#3D7A52]" /></div>
            <div className="text-4xl font-medium text-[#3D7A52] mb-1"><AnimatedCounter value={progress?.overall?.percentage || 0} suffix="%" /></div>
            <div className="text-sm text-[#888780]">Progresso globale</div>
          </div>
          <div className="sapiens-card p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#2A4A35] flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-6 h-6 text-[#3D7A52]" /></div>
            <div className="text-4xl font-medium text-[#E8E8E4] mb-1"><AnimatedCounter value={progress?.overall?.completed || 0} /></div>
            <div className="text-sm text-[#888780]">Lezioni completate</div>
          </div>
          <div className="sapiens-card p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mx-auto mb-4"><Flame className="w-6 h-6 text-[#C9A84C]" /></div>
            <div className="text-4xl font-medium text-[#C9A84C] mb-1"><AnimatedCounter value={progress?.streak || 0} /></div>
            <div className="text-sm text-[#888780]">Giorni di streak</div>
          </div>
        </motion.div>

        {user?.tier !== 'premium' && <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-8"><PremiumBanner onUpgrade={handleUpgrade} /></motion.div>}

        {/* Strumenti personali */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }} className="grid sm:grid-cols-2 gap-4 mb-8">
          <button onClick={() => setShowPercorso(true)} data-testid="percorso-personalizzato-btn"
            className="flex items-center gap-4 p-5 rounded-2xl text-left transition-all group hover:border-[#C9A84C]/40"
            style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(201,168,76,0.12)' }}>
              <Map className="w-5 h-5 text-[#C9A84C]" />
            </div>
            <div>
              <p className="font-semibold text-[#E8E8E4] text-sm">Il tuo percorso personalizzato</p>
              <p className="text-xs text-[#888780] mt-0.5">5 domande per ricevere il piano su misura per te</p>
            </div>
          </button>
          <button onClick={handleDownloadPDF} disabled={pdfLoading} data-testid="pdf-report-btn"
            className="flex items-center gap-4 p-5 rounded-2xl text-left transition-all group hover:border-[#3D7A52]/40 relative overflow-hidden"
            style={{ background: user?.tier === 'premium' ? 'rgba(61,122,82,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${user?.tier === 'premium' ? 'rgba(61,122,82,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: user?.tier === 'premium' ? 'rgba(61,122,82,0.15)' : 'rgba(255,255,255,0.05)' }}>
              <FileText className={`w-5 h-5 ${user?.tier === 'premium' ? 'text-[#3D7A52]' : 'text-[#888780]'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className={`font-semibold text-sm ${user?.tier === 'premium' ? 'text-[#E8E8E4]' : 'text-[#888780]'}`}>
                  {pdfLoading ? 'Generazione in corso...' : 'Report finanziario PDF'}
                </p>
                {user?.tier !== 'premium' && <span className="premium-badge text-[10px]">Premium</span>}
              </div>
              <p className="text-xs text-[#888780] mt-0.5">{user?.tier === 'premium' ? 'Scarica la tua fotografia finanziaria' : 'Disponibile con il piano Premium'}</p>
            </div>
            {user?.tier !== 'premium' && <div className="absolute inset-0 rounded-2xl" style={{ background: 'rgba(28,28,26,0.4)' }} />}
          </button>
        </motion.div>

        {/* Percorso modal */}
        <AnimatePresence>
          {showPercorso && <PercorsoPersonalizzato user={user} onClose={() => setShowPercorso(false)} />}
        </AnimatePresence>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="sapiens-card p-6">
              <h2 className="text-lg font-medium text-[#E8E8E4] mb-6 flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#3D7A52]" />Percorsi di apprendimento</h2>
              <div className="space-y-5">
                {levelGroups.map((group, index) => {
                  const isPremiumLocked = group.level > 2 && user?.tier !== 'premium';
                  return (
                    <motion.div key={group.level} className={isPremiumLocked ? 'opacity-50' : ''} initial={{ opacity: 0, x: -20 }} animate={{ opacity: isPremiumLocked ? 0.5 : 1, x: 0 }} transition={{ delay: 0.3 + index * 0.1 }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3"><LevelBadge level={group.level} />{isPremiumLocked && <span className="premium-badge text-[10px]">Premium</span>}</div>
                        <span className="text-sm text-[#888780] tabular-nums">{group.progress.completed}/{group.progress.total}</span>
                      </div>
                      <ProgressBar value={group.progress.percentage} size="small" />
                    </motion.div>
                  );
                })}
              </div>
              <Button className="w-full mt-6 sapiens-btn-secondary" onClick={() => navigate('/percorsi')} data-testid="view-all-lessons-btn">Vedi tutti i percorsi<ChevronRight className="w-4 h-4 ml-2" /></Button>
            </motion.div>

            {uncompletedLessons.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="sapiens-card p-6">
                <h2 className="text-lg font-medium text-[#E8E8E4] mb-6">Continua a imparare</h2>
                <div className="space-y-3">
                  {uncompletedLessons.map((lesson) => (
                    <Link key={lesson.lesson_id} to={`/lezione/${lesson.lesson_id}`} className="flex items-center justify-between p-4 bg-[#1C1C1A] rounded-xl border border-white/[0.08] hover:border-[#3D7A52]/50 transition-all group card-hover" data-testid={`lesson-link-${lesson.lesson_id}`}>
                      <div><LevelBadge level={lesson.level} /><h3 className="text-[#E8E8E4] font-medium mt-2">{lesson.title}</h3><p className="text-sm text-[#888780]">{lesson.estimated_time} min</p></div>
                      <ChevronRight className="w-5 h-5 text-[#888780] group-hover:text-[#C9A84C] transition-colors" />
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <div className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="sapiens-card p-6">
              <h2 className="text-lg font-medium text-[#E8E8E4] mb-6 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#C9A84C]" />I miei badge</h2>
              {(() => {
                const v2 = Array.isArray(user?.badges_v2) ? user.badges_v2 : [];
                const fmtDate = (iso) => {
                  try { const d = new Date(iso); const dd = String(d.getDate()).padStart(2,'0'); const mm = String(d.getMonth()+1).padStart(2,'0'); return `${dd}/${mm}/${d.getFullYear()}`; } catch { return ''; }
                };
                if (v2.length === 0 && !(progress?.badges?.length)) {
                  return <p className="text-sm text-[#888780] italic" data-testid="badges-empty">Completa i quiz per guadagnare i tuoi primi badge.</p>;
                }
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="badges-list">
                    {v2.map(b => (
                      <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }} data-testid={`badge-card-${b.id}`}>
                        <div className="text-2xl">🏆</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#E8E8E4]">Esperto di Busta Paga</p>
                          <p className="text-xs text-[#888780]" data-testid={`badge-date-${b.id}`}>{fmtDate(b.earned_at)}</p>
                        </div>
                      </div>
                    ))}
                    <div className="grid grid-cols-3 gap-2 sm:col-span-2 mt-2">
                      {allBadges.map((badge) => <BadgeDisplay key={badge} badge={badge} earned={progress?.badges?.includes(badge)} />)}
                    </div>
                  </div>
                );
              })()}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="sapiens-card p-6">
              <h2 className="text-lg font-medium text-[#E8E8E4] mb-6">Statistiche</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center"><span className="text-[#888780]">Lezioni totali</span><span className="font-medium tabular-nums text-[#E8E8E4]">{progress?.overall?.total || 0}</span></div>
                <div className="flex justify-between items-center"><span className="text-[#888780]">Quiz completati</span><span className="font-medium tabular-nums text-[#E8E8E4]">{Object.keys(progress?.quiz_scores || {}).length}</span></div>
                <div className="flex justify-between items-center"><span className="text-[#888780]">Badge guadagnati</span><span className="font-medium tabular-nums text-[#E8E8E4]">{progress?.badges?.length || 0}</span></div>
              </div>
            </motion.div>

            {/* I MIEI CERTIFICATI */}
            <MieiCertificati user={user} />

            {/* PROGRAMMA REFERRAL */}
            <ReferralPanel user={user} />

            {/* I MIEI CALCOLI (Premium) */}
            <MieiCalcoli user={user} navigate={navigate} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
